import React, { useState, FC, useEffect, useId, useCallback } from 'react';
import { Props } from './types';
import { FilterButton } from '../FilterButton';
import { FilterMenu } from '../FilterMenu';
import { noop } from '../../../utils/noop';
import { closeAllOtherFilters, registerFilter, unregisterFilter } from './filterRegistry';

export const BaseFilter: FC<Props> = props => {
  const {
    children,
    active,
    disabled,
    isOpen: overrideIsOpen,
    label,
    onToggleOpen = noop,
    selected,
    selectedIcon,
    selectedIconFamily,
    isWrapper,
    dataObjectType,
    dataAction,
    hideFilterMenu = false,
    size,
  } = props;

  let [isOpen, setIsOpen] = useState(false);
  const filterId = useId();
  const toggleOpen = useCallback(
    (nextIsOpen: boolean) => {
      if (nextIsOpen) {
        closeAllOtherFilters(filterId);
      }

      setIsOpen(nextIsOpen);
      onToggleOpen(nextIsOpen);
    },
    [onToggleOpen]
  );

  useEffect(() => {
    if (isWrapper) return;

    registerFilter(filterId, () => toggleOpen(false));
    return () => {
      unregisterFilter(filterId);
    };
  }, [filterId, isWrapper, toggleOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        toggleOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, toggleOpen]);

  if (isWrapper) {
    return <>{children}</>;
  }

  if (typeof overrideIsOpen !== 'undefined') {
    isOpen = overrideIsOpen;
  }

  return (
    <div className="filter-pill" data-object-type={dataObjectType} data-action={dataAction}>
      <FilterButton
        label={label}
        selected={selected}
        // @ts-ignore Remove once FilterButton is converted to TypeScript
        icon={selectedIcon}
        iconFamily={selectedIconFamily}
        disabled={disabled}
        onClick={() => toggleOpen(true)}
        highlighted={active}
        {...(size ? { size } : {})}
      />
      {isOpen && <FilterMenu className={hideFilterMenu ? 'hide-filter-menu' : ''}>{children}</FilterMenu>}
      {isOpen && <div onClick={() => toggleOpen(false)} className="filter-blocker" />}
    </div>
  );
};
